import { Injectable } from '@angular/core'
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js'
import { environment } from 'src/environments/environment'
import { Observable, ReplaySubject } from 'rxjs'
import { Router } from '@angular/router'

/**
 * Represents the result of a login attempt
 */
export interface LoginResult
{
    user: CognitoUser
    newPasswordRequired: boolean
}

/**
 * Enum representing authentication errors
 */
export enum AuthError
{
    PasswordResetRequired = "PasswordResetRequired",
    NoUserSessionFound = "NoUserSessionFound"
}

/**
 * Service responsible for managing user authentication with AWS Cognito.
 * Handles login, logout, password management, and session restoration.
 */
@Injectable({
    providedIn: 'root'
})
export class UserAuthService
{
    // Cognito pool configuration
    private static _poolData = {
        UserPoolId: environment.userpoolID,
        ClientId: environment.clientId
    }

    private _userPool: CognitoUserPool

    // Observable token that can be subscribed to for auth state changes
    private _token = new ReplaySubject<string | null>(1)

    // Timestamp when the current token expires
    private tokenExpiryTimestamp: number | null = null

    // Timer used to automatically log out the user when the token expires
    private logoutTimer: any = null

    /**
     * Initializes the service, sets up Cognito User Pool, and tries to restore a previous session.
     */
    constructor(private router: Router)
    {
        this._userPool = new CognitoUserPool(UserAuthService._poolData)

        // Restore session from previous login if available
        this.restoreSession()
    }

    /**
     * Observable to expose the current token, allowing other services/components to monitor auth state.
     */
    token(): Observable<string | null>
    {
        return this._token.asObservable()
    }

    /**
     * Attempts to log the user in using username and password.
     */
    async login(username: string, password: string): Promise<LoginResult>
    {
        const authDetails = new AuthenticationDetails({
            Username: username,
            Password: password
        })

        const user = new CognitoUser({
            Username: username,
            Pool: this._userPool
        })

        return new Promise((resolve, reject) =>
        {
            user.authenticateUser(authDetails, {
                onSuccess: async (session) =>
                {
                    this.processNewSession(session)
                    resolve({ user, newPasswordRequired: false })
                },
                onFailure: (err) => reject(err),
                newPasswordRequired: (userAttributes, requiredAttributes) =>
                {
                    resolve({ user, newPasswordRequired: true })
                }
            })
        })
    }

    /**
     * Completes the new password challenge for users who are required to set a new password on first login.
     */
    async completePasswordChallenge(loginResult: LoginResult, newPassword: string): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            loginResult.user.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: () => resolve(),
                onFailure: (err) => reject(err)
            })
        })
    }

    /**
     * Starts the forgotten password flow by sending a verification code to the user.
     */
    async beginForgottenPasswordFlow(username: string): Promise<CognitoUser>
    {
        const user = new CognitoUser({
            Username: username,
            Pool: this._userPool
        })

        return new Promise((resolve, reject) =>
        {
            user.forgotPassword({
                onSuccess: () => reject("Unexpected success"),
                onFailure: (err) => reject(err),
                inputVerificationCode: () => resolve(user)
            })
        })
    }

    /**
     * Completes the forgotten password flow by verifying the code and setting a new password.
     */
    async finishForgottenPasswordFlow(username: string, code: string, newPassword: string): Promise<void>
    {
        const user = new CognitoUser({
            Username: username,
            Pool: this._userPool
        })

        return new Promise((resolve, reject) =>
        {
            user.confirmPassword(code, newPassword, {
                onSuccess: () => resolve(),
                onFailure: (err) => reject(err)
            })
        })
    }

    /**
     * Retrieves the current valid token, if available.
     * Automatically logs out the user if the token is expired.
     */
    async getToken(): Promise<string | null>
    {
        const session = await this.getLoggedInUserSession()

        if (!session)
        {
            return null
        }

        if (this.isTokenExpired())
        {
            this.signOut()
            return null
        }

        return session.getIdToken().getJwtToken()
    }

    /**
     * Checks whether the user is currently authenticated.
     */
    async isAuthenticated(): Promise<boolean>
    {
        const token = await this.getToken()
        return !!token
    }

    /**
     * Retrieves the current user's session from Cognito if one exists.
     */
    private async getLoggedInUserSession(): Promise<CognitoUserSession | null>
    {
        const user = this._userPool.getCurrentUser()

        if (!user)
        {
            return null
        }

        return new Promise((resolve, reject) =>
        {
            user.getSession((err, session) =>
            {
                if (err || !session.isValid())
                {
                    resolve(null)
                }
                else
                {
                    resolve(session)
                }
            })
        })
    }

    /**
     * Processes a newly received session, updates token and schedules auto-logout.
     */
    private processNewSession(session: CognitoUserSession)
    {
        const token = session.getIdToken().getJwtToken()

        this._token.next(token)

        this.tokenExpiryTimestamp = this.getExpiryFromToken(token)

        this.scheduleAutoLogout()

        localStorage.setItem('tokenExpiry', this.tokenExpiryTimestamp.toString())
    }

    /**
     * Parses the JWT token to extract the expiration timestamp.
     */
    private getExpiryFromToken(token: string): number
    {
        const payload = JSON.parse(atob(token.split('.')[1]))

        return payload.exp * 1000
    }

    /**
     * Sets up a timer to automatically log out the user when the token expires.
     */
    private scheduleAutoLogout()
    {
        if (this.logoutTimer)
        {
            clearTimeout(this.logoutTimer)
        }

        const delay = (this.tokenExpiryTimestamp ?? 0) - Date.now()

        if (delay > 0)
        {
            this.logoutTimer = setTimeout(() => this.signOut(), delay)
        }
    }

    /**
     * Checks if the current token is expired.
     */
    private isTokenExpired(): boolean
    {
        if (!this.tokenExpiryTimestamp)
        {
            return true
        }

        return Date.now() >= this.tokenExpiryTimestamp
    }

    /**
     * Tries to restore the user's session if they were previously logged in.
     */
    private async restoreSession()
    {
        const session = await this.getLoggedInUserSession()

        if (session)
        {
            this.processNewSession(session)
        }
        else
        {
            this._token.next(null)
        }
    }

    /**
     * Logs the user out, clears token and session data, and redirects to the login page.
     */
    signOut()
    {
        const user = this._userPool.getCurrentUser()

        if (user)
        {
            user.signOut()
        }

        this._token.next(null)

        this.tokenExpiryTimestamp = null

        clearTimeout(this.logoutTimer)

        this.router.navigate(['login'])
    }
}